import { FC, useMemo, useState } from 'react'
import { useLazyQuery, useQuery } from '@apollo/client'
import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  Empty,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd'
import { ExperimentOutlined } from '@ant-design/icons'
import { translate } from '../../../core'
import { useStatesForCountry } from '../hook/useStatesForCountry'
import {
  EvaluateShippingMethodsQuery,
  GetShippingCountriesQuery,
} from '../graphql/shipping-method.graphql'
import {
  EligibleShippingMethod,
  SHIPPING_FALLBACK_REASON_COLOR,
  SHIPPING_METHOD_KIND_COLOR,
  SHIPPING_METHOD_KIND_LABEL,
  ShippingMethodKind,
  UNITED_STATES_CODE,
} from '../type/shipping-method'

export const ShippingEligibilityPreview: FC = () => {
  const [country, setCountry] = useState<string | undefined>(UNITED_STATES_CODE)
  const [state, setState] = useState<string | undefined>()
  const [cartSubtotal, setCartSubtotal] = useState<number>(500)

  const { data: countryData } = useQuery(GetShippingCountriesQuery)
  const countries = useMemo(() => countryData?.shippingCountries ?? [], [countryData])
  const states = useStatesForCountry(country, countries)

  const [evaluate, { data, loading, called, error }] = useLazyQuery(EvaluateShippingMethodsQuery, {
    fetchPolicy: 'network-only',
  })

  const results = data?.shippingMethodsEvaluate ?? []

  const run = () =>
    evaluate({
      variables: {
        input: { country: country ?? null, state: state ?? null, cartSubtotal: cartSubtotal ?? 0 },
      },
    })

  return (
    <Collapse
      style={{ marginBottom: 16 }}
      items={[
        {
          key: 'preview',
          label: (
            <Space>
              <ExperimentOutlined />
              {translate('test_eligibility')}
            </Space>
          ),
          children: (
            <Card size="small" bordered={false}>
              <Row gutter={[12, 12]} align="bottom">
                <Col xs={24} md={7}>
                  <Typography.Text type="secondary">{translate('destination_country')}</Typography.Text>
                  <Select
                    value={country}
                    onChange={next => {
                      setCountry(next)
                      setState(undefined)
                    }}
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder={translate('any_country')}
                    style={{ width: '100%' }}
                    options={countries.map(c => ({ value: c.code, label: `${c.name} (${c.code})` }))}
                  />
                </Col>
                <Col xs={24} md={7}>
                  <Typography.Text type="secondary">{translate('destination_state')}</Typography.Text>
                  {states.length > 0 ? (
                    <Select
                      value={state}
                      onChange={setState}
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      placeholder={translate('any_state')}
                      style={{ width: '100%' }}
                      options={states.map(s => ({ value: s.key, label: `${s.name} (${s.key})` }))}
                    />
                  ) : (
                    <Input
                      value={state ?? ''}
                      onChange={e => setState(e.target.value || undefined)}
                      placeholder={translate('any_state')}
                    />
                  )}
                </Col>
                <Col xs={24} md={6}>
                  <Typography.Text type="secondary">{translate('cart_subtotal')}</Typography.Text>
                  <InputNumber
                    value={cartSubtotal}
                    onChange={value => setCartSubtotal(value ?? 0)}
                    min={0}
                    precision={2}
                    prefix="$"
                    style={{ width: '100%' }}
                  />
                </Col>
                <Col xs={24} md={4}>
                  <Button type="primary" block loading={loading} onClick={run}>
                    {translate('run')}
                  </Button>
                </Col>
              </Row>

              {error && (
                <Alert type="error" showIcon style={{ marginTop: 16 }} message={error.message} />
              )}

              {called && !loading && !error && (
                results.length === 0 ? (
                  <Empty
                    style={{ marginTop: 16 }}
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={translate('no_eligible_shipping_methods')}
                  />
                ) : (
                  <Table<EligibleShippingMethod>
                    style={{ marginTop: 16 }}
                    rowKey="id"
                    size="small"
                    pagination={false}
                    dataSource={results}
                    columns={[
                      { dataIndex: 'title', title: translate('title') },
                      {
                        dataIndex: 'key',
                        title: translate('key'),
                        render: (key: string) => <code>{key}</code>,
                      },
                      {
                        dataIndex: 'kind',
                        title: translate('shipping_method_kind'),
                        render: (kind: ShippingMethodKind) => (
                          <Tag color={SHIPPING_METHOD_KIND_COLOR[kind]}>
                            {SHIPPING_METHOD_KIND_LABEL[kind]}
                          </Tag>
                        ),
                      },
                      {
                        dataIndex: 'price',
                        title: translate('price'),
                        align: 'right',
                        render: (price: number) => `$${price.toFixed(2)}`,
                      },
                      {
                        key: 'why',
                        title: translate('why_eligible'),
                        render: (_, row) => (
                          <Space size={4}>
                            {row.matchedRuleId ? (
                              <Tag>
                                {row.matchedRuleLabel
                                  ? `${translate('matched_rule')}: ${row.matchedRuleLabel}`
                                  : `${translate('matched_rule')} #${row.matchedRuleId}`}
                              </Tag>
                            ) : (
                              <Tag color="green">{translate('no_rule_blocked_it')}</Tag>
                            )}
                            {row.fallbackApplied && (
                              <Tag color={row.fallbackReason
                                ? SHIPPING_FALLBACK_REASON_COLOR[row.fallbackReason]
                                : 'orange'}>
                                {translate('fallback_applied')}
                                {row.fallbackReason ? `: ${row.fallbackReason}` : ''}
                              </Tag>
                            )}
                          </Space>
                        ),
                      },
                    ]}
                  />
                )
              )}
            </Card>
          ),
        },
      ]}
    />
  )
}
